import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { ClinicalButton } from './ui';
import {
  buildMedicationRedaction,
  buildPatientDetailLine,
  buildPatientIdentityBlock,
  buildPrescriptionMeta,
  buildPrescriptionRefParts,
  formatClinicalRefNumber,
  formatMedicationIndexName
} from './utils/prescriptionRedaction';
import {
  buildPrescriptionBarcodeValue,
  renderPrescriptionBarcodeDataUrl
} from './utils/prescriptionBarcode';
import './ConsultationPrescriptionViewModal.css';
import { printPrescriptionA4 } from './utils/prescriptionA4Print';
import { printPrescriptionThermal } from './utils/prescriptionThermalPrint';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo_vertical_noir.png`;
const FUNTOA_SLOGAN_LINES = ['« Sécurité et santé :', 'main dans la main vers le succès »'];
const FUNTOA_SLOGAN = FUNTOA_SLOGAN_LINES.join(' ');
const FUNTOA_FOOTER_LINES = [
  'Agrément N° 221-14/MFPTLS/SG/DGTLS du 18/12/2014 – NIF 400 388 5426 – STAT 86909 31 2019 0 01640',
  'Téléphone : 032 07 366 11'
];

function formatDoctorName(name) {
  const n = (name || '').trim();
  if (!n) return null;
  return n.replace(/^Dr\.?\s*/i, '');
}

function isPhysicianRole(role) {
  const normalizedRole = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return ['doctor', 'head_doctor', 'physician', 'medecin', 'médecin'].includes(normalizedRole);
}

function MedicationList({ items = [] }) {
  if (!items.length) {
    return <p className="rx-premium__empty">—</p>;
  }

  return (
    <ol className="rx-premium__med-list">
      {items.map((item, index) => {
        const { name, qtyLine, detailLines } = buildMedicationRedaction(item);
        const hasPosology = detailLines.length > 0;
        return (
          <li key={item.medication_id || item.id || index} className="rx-premium__med-item">
            <div className="rx-premium__med-head">
              <p className="rx-premium__med-name">{formatMedicationIndexName(index, name)}</p>
              {qtyLine ? <p className="rx-premium__med-qty">{qtyLine}</p> : null}
            </div>
            {hasPosology ? (
              <div className="rx-premium__med-poso">
                <span className="rx-premium__med-poso-label">Posologie</span>
                {detailLines.map((line, i) => (
                  <p key={i} className="rx-premium__med-line">
                    {line}
                  </p>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export default function ConsultationPrescriptionViewModal({
  show,
  onHide,
  consultation,
  prescription = null,
  previewUnsaved = false
}) {
  const patient = buildPatientIdentityBlock(consultation, prescription);
  const meta = buildPrescriptionMeta(consultation, prescription);
  const notes = (prescription?.notes || '').trim();
  const items = prescription?.items || [];
  const doctorName = formatDoctorName(meta.doctorName);
  const doctorTitle = isPhysicianRole(meta.doctorRole) ? 'Dr ' : '';
  const patientDetailLine = buildPatientDetailLine(patient);
  const refParts = buildPrescriptionRefParts(meta, {
    fallbackDate: previewUnsaved ? new Date().toISOString() : null
  });
  const ordonnanceNumber =
    refParts?.prescriptionNumber || formatClinicalRefNumber(prescription?.id || meta.prescriptionId);
  const barcodeValue = buildPrescriptionBarcodeValue(refParts?.prescriptionTag, ordonnanceNumber);
  const [barcodeDataUrl, setBarcodeDataUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!show || !barcodeValue) {
      setBarcodeDataUrl(null);
      return undefined;
    }
    const url = renderPrescriptionBarcodeDataUrl(barcodeValue, {
      width: 3,
      height: 80,
      displayValue: false,
      fontSize: 18,
      margin: 10
    });
    if (!cancelled) setBarcodeDataUrl(url);
    return () => {
      cancelled = true;
    };
  }, [show, barcodeValue]);

  const runPrint = useCallback((format = 'a4') => {
    if (format === 'thermal-80') {
      const thermalPayload = {
        logoUrl: LOGO_SRC,
        siteName: meta.siteName || '',
        barcodeValue,
        slogan: FUNTOA_SLOGAN,
        sloganLines: FUNTOA_SLOGAN_LINES,
        documentTitle: 'ORDONNANCE',
        refParts: {
          ...(refParts || {}),
          prescriptionNumber: ordonnanceNumber || refParts?.prescriptionNumber || null
        },
        previewUnsaved,
        patientName: patient.name || '—',
        patientDetailLine: patientDetailLine || '',
        medications: items.map((item, index) => {
          const { name, qtyLine, detailLines } = buildMedicationRedaction(item);
          return {
            indexName: formatMedicationIndexName(index, name),
            qtyLine: qtyLine || '',
            detailLines
          };
        }),
        notes,
        doctorName: doctorName ? `${doctorTitle}${doctorName}` : '',
        footerLines: FUNTOA_FOOTER_LINES
      };
      void printPrescriptionThermal(thermalPayload, 'thermal-80');
      return;
    }

    printPrescriptionA4();
  }, [
    doctorName,
    doctorTitle,
    items,
    meta.siteName,
    notes,
    previewUnsaved,
    ordonnanceNumber,
    patient.name,
    patientDetailLine,
    barcodeValue,
    refParts
  ]);

  useEffect(() => {
    if (!show) return;
    const preload = new Image();
    preload.src = LOGO_SRC;
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="prescription-view-modal clinical-view-modal"
      aria-labelledby="prescription-view-title"
    >
      <Modal.Header closeButton className="prescription-view-modal__head recv-no-print">
        <Modal.Title id="prescription-view-title" className="prescription-view-modal__head-title">
          {ordonnanceNumber ? `Ordonnance ${ordonnanceNumber}` : 'Ordonnance'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="prescription-view-modal__body">
        {previewUnsaved ? (
          <Alert variant="warning" className="py-2 small recv-no-print mb-3">
            Aperçu — enregistrez l&apos;ordonnance avant remise au patient.
          </Alert>
        ) : null}

        <article className="rx-premium" id="prescription-print-sheet">
          <div className="rx-premium__band" aria-hidden="true" />

          <header className="rx-premium__header">
            <div className="rx-premium__top">
              <img src={LOGO_SRC} alt="FUNTOA SMIE — Service Médical Interentreprise" className="rx-premium__logo" />
              <div className="rx-premium__brand-center">
                <p className="rx-premium__slogan">
                  {FUNTOA_SLOGAN_LINES.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </p>
                <h1 className="rx-premium__doc-title">Ordonnance</h1>
              </div>
              <div className="rx-premium__site-block">
                {meta.siteName ? <p className="rx-premium__site-top">{meta.siteName}</p> : null}
              </div>
            </div>
            <div className="rx-premium__reference">
              <p className="rx-premium__doc-number">
                {ordonnanceNumber || (previewUnsaved ? 'N° attribué après enregistrement' : 'N° indisponible')}
              </p>
              {barcodeDataUrl ? (
                <img
                  src={barcodeDataUrl}
                  alt={`Code-barres ordonnance ${ordonnanceNumber || ''}`.trim()}
                  className="rx-premium__barcode"
                />
              ) : (
                <p className="rx-premium__barcode-status">
                  {previewUnsaved ? 'Code-barres généré après enregistrement' : 'Code-barres indisponible'}
                </p>
              )}
            </div>
            <div className="rx-premium__header-rule" aria-hidden="true" />
          </header>

          <div className="rx-premium__content">
            <div className="rx-premium__fields">
              <div className="rx-premium__field">
                <span className="rx-premium__field-label">Nom</span>
                <span className="rx-premium__field-value">{patient.name}</span>
              </div>
              {patientDetailLine ? (
                <div className="rx-premium__field">
                  <span className="rx-premium__field-label">Infos</span>
                  <span className="rx-premium__field-value rx-premium__field-value--meta">{patientDetailLine}</span>
                </div>
              ) : null}
              {refParts?.date ? (
                <div className="rx-premium__field">
                  <span className="rx-premium__field-label">Date</span>
                  <span className="rx-premium__field-value rx-premium__field-value--meta">{refParts.date}</span>
                </div>
              ) : null}
            </div>

            <MedicationList items={items} />

            {notes ? (
              <div className="rx-premium__notes">
                <span className="rx-premium__notes-label">Notes</span>
                <p className="rx-premium__notes-body">{notes}</p>
              </div>
            ) : null}
          </div>

          <footer className="rx-premium__footer">
            <div className="rx-premium__sign">
              <p className="rx-premium__stamp-label">Signature</p>
              <div className="rx-premium__stamp" aria-hidden="true" />
              {doctorName ? <p className="rx-premium__doctor">{doctorTitle}{doctorName}</p> : null}
            </div>

            <div className="rx-premium__band rx-premium__band--footer" aria-hidden="true" />
            <div className="rx-premium__legal">
              {FUNTOA_FOOTER_LINES.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </footer>
        </article>
      </Modal.Body>

      <Modal.Footer className="prescription-view-modal__foot recv-no-print">
        <ClinicalButton variant="ghost" size="sm" onClick={onHide}>
          Fermer
        </ClinicalButton>
        <ClinicalButton variant="secondary" size="sm" onClick={() => runPrint('a4')}>
          Imprimer A4 / PDF
        </ClinicalButton>
        <ClinicalButton variant="primary" size="sm" onClick={() => runPrint('thermal-80')}>
          Ticket 80&nbsp;mm
        </ClinicalButton>
      </Modal.Footer>
    </Modal>
  );
}
